import { sumArray } from '../../../../functions/utilities/array-utility';
import { CustomTargetModelData } from '../../../../model/custom-model/custom-target-model-data';
import { Dict } from '../../../../model/dict';
import { CustomModelWithAbatementPerCcc } from './dashboard-calculation.service';

export function getCustomModelsWithAbatementPerCcc(
  customModelData: (CustomTargetModelData & { id: string })[],
  year: number,
  cccEmissions: Dict<number>
): CustomModelWithAbatementPerCcc[] {
  return customModelData.map((cm) => {
    const totalAbatement = cm.chartData.carbonAbatement[year] ?? 0;
    const weights =
      cm.lhsCccs?.reduce((weights: Dict<number>, ccc) => {
        weights[ccc] = cccEmissions[ccc] ?? 0;
        return weights;
      }, {}) ?? {};
    const distributedAbatement = distributeAbatement(totalAbatement, weights);

    const abatements =
      cm.lhsCccs?.reduce((abatements: Dict<number>, ccc) => {
        abatements[ccc] = distributedAbatement[ccc] ?? 0;
        return abatements;
      }, {}) ?? {};

    const cccAbatements =
      cm.lhsCccs?.map((ccc) => {
        return { ccc, abatement: abatements[ccc] ?? 0 };
      }) ?? [];
    return {
      ...cm,
      cccAbatements,
    };
  });
}

export function distributeAbatement(totalAbatement: number, emissions: Dict<number>): Dict<number> {
  const applyAbatement = (abatement: number, emissions: Dict<number>) => {
    const allWeights = Object.values(emissions).map((w) => Math.max(0, w));
    const allKeys = Object.keys(emissions);
    const totalWeights = allWeights.reduce((acc, w) => acc + w, 0);
    return allKeys.reduce((abatements: Dict<number>, ccc, i) => {
      const weight = allWeights[i];
      const part = totalWeights === 0 ? 1 / allWeights.length : weight / totalWeights;
      abatements[ccc] = part * abatement;
      return abatements;
    }, {});
  };
  const totalWeight = sumArray(Object.values(emissions).map((w) => Math.max(0, w)));
  if (totalWeight < totalAbatement) {
    //we first apply to remove all emissions.
    const first = applyAbatement(totalWeight, emissions);
    totalAbatement = totalAbatement - totalWeight;
    emissions = Object.keys(emissions).reduce((acc: Dict<number>, key) => {
      acc[key] = 0;
      return acc;
    }, {});
    return joinAbatementDicts(first, applyAbatement(totalAbatement, emissions));
  }
  return applyAbatement(totalAbatement, emissions);
}

function joinAbatementDicts(dict1: Dict<number>, dict2: Dict<number>): Dict<number> {
  const keys = new Set([...Object.keys(dict1), ...Object.keys(dict2)]);
  return Array.from(keys).reduce((acc: Dict<number>, key) => {
    acc[key] = (dict1[key] || 0) + (dict2[key] || 0);
    return acc;
  }, {});
}
